import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { WhatsAppConnection } from '@/types';
import { apiCall } from '@/services/api';

interface CreateConnectionData {
  tenant_id?: string;
  loja_id: string | null;
  instance_name: string;
  canal_origem?: string | null;
  numero_instancia?: string | null;
}

export interface AvailableInstance {
  name: string;
  token: string;
  connectionStatus: string;
  ownerJid?: string | null;
  profileName?: string | null;
  profilePicUrl?: string | null;
}

interface WhatsAppStoreState {
  connections: WhatsAppConnection[];
  loading: boolean;
  error: string | null;
  fetchConnections: () => Promise<void>;
  fetchAvailableInstances: () => Promise<AvailableInstance[]>;
  createConnection: (data: CreateConnectionData) => Promise<WhatsAppConnection | null>;
  connectInstance: (id: string) => Promise<{ qrCode: string | null; status: string }>;
  getStatus: (id: string) => Promise<{ status: string; phone_number?: string | null; business_name?: string | null }>;
  configureWebhook: (id: string) => Promise<{ success: boolean; webhook_url?: string }>;
  disconnect: (id: string) => Promise<boolean>;
  deleteConnection: (id: string) => Promise<boolean>;
  refreshQR: (id: string) => Promise<{ qrCode: string | null; status: string }>;
  importConnection: (data: { instance_token: string; name: string; loja_id: string | null; tenant_id?: string }) => Promise<WhatsAppConnection | null>;
}

export const useWhatsAppStore = create<WhatsAppStoreState>((set, get) => ({
  connections: [],
  loading: false,
  error: null,

  fetchConnections: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiCall('/api/whatsapp/connections');
      if (res.ok) {
        const list = await res.json();
        set({ connections: Array.isArray(list) ? list : [], loading: false });
      } else {
        const errData = await res.json().catch(() => ({}));
        set({ error: errData.error || 'Erro ao carregar conexões WhatsApp', loading: false });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error fetching connections';
      console.error('[WHATSAPP_STORE] Exception:', err);
      set({ error: message, loading: false });
    }
  },

  fetchAvailableInstances: async () => {
    try {
      const res = await apiCall('/api/whatsapp/instances-available', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to fetch available instances');
      }
      const data = await res.json();
      return data;
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] fetchAvailableInstances error:', err);
      throw err;
    }
  },

  createConnection: async (payload: CreateConnectionData) => {
    try {
      const res = await apiCall('/api/whatsapp/connections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to create connection');
      }
      const newConn: WhatsAppConnection = await res.json();
      await get().fetchConnections();
      return newConn;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error creating connection';
      console.error('[WHATSAPP_STORE] createConnection error:', message);
      throw err;
    }
  },

  connectInstance: async (id: string) => {
    try {
      const res = await apiCall(`/api/whatsapp/connections/${id}/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to connect instance');
      }
      const data = await res.json();
      await get().fetchConnections();
      return { qrCode: data.qrCode || data.qrcode || null, status: data.status || 'qrcode' };
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] connectInstance error:', err);
      throw err;
    }
  },

  getStatus: async (id: string) => {
    try {
      const res = await apiCall(`/api/whatsapp/connections/${id}/status`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to get status');
      }
      const data = await res.json();
      set(state => ({
        connections: state.connections.map(c => 
          c.id === id 
            ? { ...c, status: data.status || c.status, phone_number: data.phone_number || c.phone_number } 
            : c
        )
      }));
      return data;
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] getStatus error:', err);
      throw err;
    }
  },

  configureWebhook: async (id: string) => {
    try {
      const res = await apiCall(`/api/whatsapp/connections/${id}/configure-webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to configure webhook');
      }
      const data = await res.json();
      await get().fetchConnections();
      return data;
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] configureWebhook error:', err);
      throw err;
    }
  },

  disconnect: async (id: string) => {
    try {
      const res = await apiCall(`/api/whatsapp/connections/${id}/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to disconnect');
      }
      await get().fetchConnections();
      return true;
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] disconnect error:', err);
      throw err;
    }
  },

  deleteConnection: async (id: string) => {
    try {
      const res = await apiCall(`/api/whatsapp/connections/${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to delete connection');
      }
      set(state => ({
        connections: state.connections.filter(c => c.id !== id)
      }));
      return true;
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] deleteConnection error:', err);
      throw err;
    }
  },

  refreshQR: async (id: string) => {
    try {
      const res = await apiCall(`/api/whatsapp/connections/${id}/refresh-qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to refresh QR code');
      }
      const data = await res.json();
      await get().fetchConnections();
      return { qrCode: data.qrCode || data.qrcode || null, status: data.status || 'qrcode' };
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] refreshQR error:', err);
      throw err;
    }
  },

  importConnection: async (payload) => {
    try {
      const res = await apiCall('/api/whatsapp/connections/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to import connection');
      }
      const data = await res.json();
      await get().fetchConnections();
      return data.connection || data;
    } catch (err: unknown) {
      console.error('[WHATSAPP_STORE] importConnection error:', err);
      throw err;
    }
  }
}));
